import axios, { CancelTokenSource } from 'axios';
import { Action, Dispatch } from 'redux';

import * as groupService from '../../services/group';
import * as meetingService from '../../services/meeting';
import * as messagesService from '../../services/messages';
import * as personService from '../../services/person';

import { GroupActionTypes } from './groupActionTypes';
import { IGroup, IGroupMember, IMeeting, IGroupMessage } from './groupState';

export interface ICancelPersonStarted extends Action<typeof GroupActionTypes.CANCEL_PERSON_STARTED> {
    payload: { groupId: string, meetingId: string, personId: number }
}

export interface ICancelPersonCompleted extends Action<typeof GroupActionTypes.CANCEL_PERSON_COMPLETED> {
    payload: { groupId: string, meetingId: string, personId: number }
}

export interface ICancelPersonFailed extends Action<typeof GroupActionTypes.CANCEL_PERSON_FAILED> {
    payload: { groupId: string, meetingId: string, personId: number, error: any }
}

export interface ISignupPersonStarted extends Action<typeof GroupActionTypes.SIGNUP_PERSON_STARTED> {
    payload: { groupId: string, meetingId: string, personId: number }
}

export interface ISignupPersonCompleted extends Action<typeof GroupActionTypes.SIGNUP_PERSON_COMPLETED> {
    payload: { groupId: string, meetingId: string, personId: number }
}

export interface ISignupPersonFailed extends Action<typeof GroupActionTypes.SIGNUP_PERSON_FAILED> {
    payload: { groupId: string, meetingId: string, personId: number, error: any }
}

export interface IFetchGroupMeetingStarted extends Action<typeof GroupActionTypes.FETCH_GROUP_MEETING_STARTED> {
    payload: { groupId: string, meetingId: string }
}

export interface IFetchGroupMeetingCompleted extends Action<typeof GroupActionTypes.FETCH_GROUP_MEETING_COMPLETED> {
    payload: {
        groupId: string,
        meetingId: string,
        meeting: IMeeting;
    }
}

export interface IFetchGroupMeetingFailed extends Action<typeof GroupActionTypes.FETCH_GROUP_MEETING_FAILED> {
    payload: { groupId: string, meetingId: string }
}

export interface IRemoveMeetingDocumentStarted extends Action<typeof GroupActionTypes.REMOVE_MEETING_DOCUMENT_STARTED> {
    payload: { groupId: string, meetingId: string, documentId: string }
}

export interface IRemoveMeetingDocumentCompleted extends Action<typeof GroupActionTypes.REMOVE_MEETING_DOCUMENT_COMPLETED> {
    payload: {
        groupId: string,
        meetingId: string,
        documentId: string;
    }
}

export interface IRemoveMeetingDocumentFailed extends Action<typeof GroupActionTypes.REMOVE_MEETING_DOCUMENT_FAILED> {
    payload: { groupId: string, meetingId: string, documentId: string, error: any }
}

export interface IAddFavoriteAttachmentStarted extends Action<typeof GroupActionTypes.ADD_FAVORITE_ATTACHMENT_STARTED> {
    payload: { groupId: string, messageId: string, attachmentId: string }
}

export interface IAddFavoriteAttachmentCompleted extends Action<typeof GroupActionTypes.ADD_FAVORITE_ATTACHMENT_COMPLETED> {
    payload: { groupId: string, messageId: string, attachmentId: string }
}

export interface IAddFavoriteAttachmentFailed extends Action<typeof GroupActionTypes.ADD_FAVORITE_ATTACHMENT_FAILED> {
    payload: { groupId: string, messageId: string, attachmentId: string }
}

export interface IRemoveFavoriteAttachmentStarted extends Action<typeof GroupActionTypes.REMOVE_FAVORITE_ATTACHMENT_STARTED> {
    payload: { groupId: string, messageId: string, attachmentId: string }
}

export interface IRemoveFavoriteAttachmentCompleted extends Action<typeof GroupActionTypes.REMOVE_FAVORITE_ATTACHMENT_COMPLETED> {
    payload: { groupId: string, messageId: string, attachmentId: string }
}

export interface IRemoveFavoriteAttachmentFailed extends Action<typeof GroupActionTypes.REMOVE_FAVORITE_ATTACHMENT_FAILED> {
    payload: { groupId: string, messageId: string, attachmentId: string }
}

export interface IFetchGroupMeetingsStarted extends Action<typeof GroupActionTypes.FETCH_GROUP_MEETINGS_STARTED> {
    payload: string
}

export interface IFetchGroupMeetingsCompleted extends Action<typeof GroupActionTypes.FETCH_GROUP_MEETINGS_COMPLETED> {
    payload: {
        id: string,
        meetings: IMeeting[];
    }
}

export interface IFetchGroupMeetingsFailed extends Action<typeof GroupActionTypes.FETCH_GROUP_MEETINGS_FAILED> {
    payload: string
}

export interface IAddFavoriteDocumentStarted extends Action<typeof GroupActionTypes.ADD_FAVORITE_DOCUMENT_STARTED> {
    payload: { groupId: string, meetingId: string, documentId: string }
}

export interface IAddFavoriteDocumentCompleted extends Action<typeof GroupActionTypes.ADD_FAVORITE_DOCUMENT_COMPLETED> {
    payload: { groupId: string, meetingId: string, documentId: string }
}

export interface IAddFavoriteDocumentFailed extends Action<typeof GroupActionTypes.ADD_FAVORITE_DOCUMENT_FAILED> {
    payload: { groupId: string, meetingId: string, documentId: string }
}

export interface IRemoveFavoriteDocumentStarted extends Action<typeof GroupActionTypes.REMOVE_FAVORITE_DOCUMENT_STARTED> {
    payload: { groupId: string, meetingId: string, documentId: string }
}

export interface IRemoveFavoriteDocumentCompleted extends Action<typeof GroupActionTypes.REMOVE_FAVORITE_DOCUMENT_COMPLETED> {
    payload: { groupId: string, meetingId: string, documentId: string }
}

export interface IRemoveFavoriteDocumentFailed extends Action<typeof GroupActionTypes.REMOVE_FAVORITE_DOCUMENT_FAILED> {
    payload: { groupId: string, meetingId: string, documentId: string }
}

export interface IFetchGroupMembersStarted extends Action<typeof GroupActionTypes.FETCH_GROUP_MEMBERS_STARTED> {
    payload: string
}

export interface IFetchGroupMembersCompleted extends Action<typeof GroupActionTypes.FETCH_GROUP_MEMBERS_COMPLETED> {
    payload: {
        id: string,
        members: IGroupMember[];
    }
}

export interface IFetchGroupMembersFailed extends Action<typeof GroupActionTypes.FETCH_GROUP_MEMBERS_FAILED> {
    payload: string
}

export interface IFetchGroupActivityStatsCompleted extends Action<typeof GroupActionTypes.FETCH_GROUP_ACTIVITYSTATS_COMPLETED> {
    payload: { groupId: string, from: Date, result: any}
}

export interface IFetchGroupActivityStatsFailed extends Action<typeof GroupActionTypes.FETCH_GROUP_ACTIVITYSTATS_FAILED> {
    payload: { groupId: string, from: Date, error: any }
}

export interface IFetchGroupMessagesStarted extends Action<typeof GroupActionTypes.FETCH_GROUP_MESSAGES_STARTED> {
    payload: string
}

export interface IFetchGroupMessagesCompleted extends Action<typeof GroupActionTypes.FETCH_GROUP_MESSAGES_COMPLETED> {
    payload: {
        id: string,
        messages: IGroupMessage[];
    }
}

export interface IFetchGroupMessagesFailed extends Action<typeof GroupActionTypes.FETCH_GROUP_MESSAGES_FAILED> {
    payload: string
}

export interface IFetchGroupsStarted extends Action<typeof GroupActionTypes.FETCH_GROUPS_STARTED> { }

export interface IFetchGroupsCompleted extends Action<typeof GroupActionTypes.FETCH_GROUPS_COMPLETED> {
    payload: {
        groups: IGroup[],
        isDi: boolean;
    }
}

export interface IFetchGroupsFailed extends Action<typeof GroupActionTypes.FETCH_GROUPS_FAILED> { }

export interface IGetGroupStarted extends Action<typeof GroupActionTypes.GET_GROUP_STARTED> {
    payload: string
}

export interface IGetGroupCompleted extends Action<typeof GroupActionTypes.GET_GROUP_COMPLETED> {
    payload: {
        groupId: string;
        group: IGroup;
    }
}

export interface IGetGroupFailed extends Action<typeof GroupActionTypes.GET_GROUP_FAILED> {
    payload: {
        groupId: string;
        error: any;
    }
}

export interface ISetCurrentGroup extends Action<typeof GroupActionTypes.SET_CURRENT_GROUP> {
    payload: string
}

export type GroupActions = IFetchGroupMeetingStarted
    | ISignupPersonStarted
    | ISignupPersonCompleted
    | ISignupPersonFailed
    | ICancelPersonStarted
    | ICancelPersonCompleted
    | ICancelPersonFailed
    | IRemoveMeetingDocumentStarted
    | IRemoveMeetingDocumentCompleted
    | IRemoveMeetingDocumentFailed
    | IAddFavoriteAttachmentStarted
    | IAddFavoriteAttachmentCompleted
    | IAddFavoriteAttachmentFailed
    | IRemoveFavoriteAttachmentStarted
    | IRemoveFavoriteAttachmentCompleted
    | IRemoveFavoriteAttachmentFailed
    | IFetchGroupMeetingCompleted
    | IFetchGroupMeetingFailed
    | IFetchGroupMeetingsStarted
    | IFetchGroupMeetingsCompleted
    | IFetchGroupMeetingsFailed
    | IFetchGroupMembersStarted
    | IFetchGroupMembersCompleted
    | IFetchGroupActivityStatsCompleted
    | IFetchGroupActivityStatsFailed
    | IFetchGroupMembersFailed
    | IFetchGroupMessagesStarted
    | IFetchGroupMessagesCompleted
    | IFetchGroupMessagesFailed
    | IFetchGroupsStarted
    | IFetchGroupsCompleted
    | IFetchGroupsFailed
    | IGetGroupStarted
    | IGetGroupCompleted
    | IGetGroupFailed
    | ISetCurrentGroup
    | IAddFavoriteDocumentStarted
    | IAddFavoriteDocumentCompleted
    | IAddFavoriteDocumentFailed
    | IRemoveFavoriteDocumentStarted
    | IRemoveFavoriteDocumentCompleted
    | IRemoveFavoriteDocumentFailed;

export const actionCreators = {
    cancelPersonStarted(groupId: string, meetingId: string, personId: number): ICancelPersonStarted {
        return {
            payload: { groupId, meetingId, personId },
            type: GroupActionTypes.CANCEL_PERSON_STARTED
        };
    },
    cancelPersonCompleted(groupId: string, meetingId: string, personId: number): ICancelPersonCompleted {
        return {
            payload: { groupId, meetingId, personId },
            type: GroupActionTypes.CANCEL_PERSON_COMPLETED
        };
    },
    cancelPersonFailed(groupId: string, meetingId: string, personId: number, error: any): ICancelPersonFailed {
        return {
            payload: { groupId, meetingId, personId, error },
            type: GroupActionTypes.CANCEL_PERSON_FAILED
        };
    },
    signupPersonStarted(groupId: string, meetingId: string, personId: number): ISignupPersonStarted {
        return {
            payload: { groupId, meetingId, personId },
            type: GroupActionTypes.SIGNUP_PERSON_STARTED
        };
    },
    signupPersonCompleted(groupId: string, meetingId: string, personId: number): ISignupPersonCompleted {
        return {
            payload: { groupId, meetingId, personId },
            type: GroupActionTypes.SIGNUP_PERSON_COMPLETED
        };
    },
    signupPersonFailed(groupId: string, meetingId: string, personId: number, error: any): ISignupPersonFailed {
        return {
            payload: { groupId, meetingId, personId, error },
            type: GroupActionTypes.SIGNUP_PERSON_FAILED
        };
    },
    removeMeetingDocumentStarted(groupId: string, meetingId: string, documentId: string): IRemoveMeetingDocumentStarted {
        return {
            payload: { groupId, meetingId, documentId },
            type: GroupActionTypes.REMOVE_MEETING_DOCUMENT_STARTED
        };
    },
    removeMeetingDocumentCompleted(groupId: string, meetingId: string, documentId: string): IRemoveMeetingDocumentCompleted {
        return {
            payload: { groupId, meetingId, documentId },
            type: GroupActionTypes.REMOVE_MEETING_DOCUMENT_COMPLETED
        };
    },
    removeMeetingDocumentFailed(groupId: string, meetingId: string, documentId: string, error: any): IRemoveMeetingDocumentFailed {
        return {
            payload: { groupId, meetingId, documentId, error },
            type: GroupActionTypes.REMOVE_MEETING_DOCUMENT_FAILED
        };
    },
    addFavoriteAttachmentStarted(groupId: string, messageId: string, attachmentId: string): IAddFavoriteAttachmentStarted {
        return {
            payload: { groupId, messageId, attachmentId },
            type: GroupActionTypes.ADD_FAVORITE_ATTACHMENT_STARTED
        };
    },
    addFavoriteAttachmentCompleted(groupId: string, messageId: string, attachmentId: string): IAddFavoriteAttachmentCompleted {
        return {
            payload: { groupId, messageId, attachmentId },
            type: GroupActionTypes.ADD_FAVORITE_ATTACHMENT_COMPLETED
        };
    },
    addFavoriteAttachmentFailed(groupId: string, messageId: string, attachmentId: string): IAddFavoriteAttachmentFailed {
        return {
            payload: { groupId, messageId, attachmentId },
            type: GroupActionTypes.ADD_FAVORITE_ATTACHMENT_FAILED
        };
    },
    removeFavoriteAttachmentStarted(groupId: string, messageId: string, attachmentId: string): IRemoveFavoriteAttachmentStarted {
        return {
            payload: { groupId, messageId, attachmentId },
            type: GroupActionTypes.REMOVE_FAVORITE_ATTACHMENT_STARTED
        };
    },
    removeFavoriteAttachmentCompleted(groupId: string, messageId: string, attachmentId: string): IRemoveFavoriteAttachmentCompleted {
        return {
            payload: { groupId, messageId, attachmentId },
            type: GroupActionTypes.REMOVE_FAVORITE_ATTACHMENT_COMPLETED
        };
    },
    removeFavoriteAttachmentFailed(groupId: string, messageId: string, attachmentId: string): IRemoveFavoriteAttachmentFailed {
        return {
            payload: { groupId, messageId, attachmentId },
            type: GroupActionTypes.REMOVE_FAVORITE_ATTACHMENT_FAILED
        };
    },
    fetchGroupMembersStarted(groupId: string): IFetchGroupMembersStarted {
        return {
            payload: groupId,
            type: GroupActionTypes.FETCH_GROUP_MEMBERS_STARTED
        };
    },
    fetchGroupMembersCompleted(groupId: string, groupMembers: IGroupMember[]): IFetchGroupMembersCompleted {
        return {
            payload: {
                id: groupId,
                members: groupMembers
            },
            type: GroupActionTypes.FETCH_GROUP_MEMBERS_COMPLETED
        };
    },
    fetchGroupMembersFailed(groupId: string, error: any): IFetchGroupMembersFailed {
        return {
            payload: groupId,
            type: GroupActionTypes.FETCH_GROUP_MEMBERS_FAILED
        };
    },
    fetchGroupActivityStatsCompleted(groupId: string, from: Date, result: any): IFetchGroupActivityStatsCompleted {
        return {
            payload: { groupId, from, result },
            type: GroupActionTypes.FETCH_GROUP_ACTIVITYSTATS_COMPLETED
        };
    },
    fetchGroupActivityStatsFailed(groupId: string, from: Date, error: any): IFetchGroupActivityStatsFailed {
        return {
            payload: { groupId, from, error },
            type: GroupActionTypes.FETCH_GROUP_ACTIVITYSTATS_FAILED
        };
    },
    fetchGroupMessagesStarted(groupId: string): IFetchGroupMessagesStarted {
        return {
            payload: groupId,
            type: GroupActionTypes.FETCH_GROUP_MESSAGES_STARTED
        };
    },
    fetchGroupMessagesCompleted(groupId: string, groupMessages: IGroupMessage[]): IFetchGroupMessagesCompleted {
        return {
            payload: {
                id: groupId,
                messages: groupMessages
            },
            type: GroupActionTypes.FETCH_GROUP_MESSAGES_COMPLETED
        };
    },
    fetchGroupMessagesFailed(groupId: string, error: any): IFetchGroupMessagesFailed {
        return {
            payload: groupId,
            type: GroupActionTypes.FETCH_GROUP_MESSAGES_FAILED
        };
    },
    fetchGroupMeetingStarted(groupId: string, meetingId: string): IFetchGroupMeetingStarted {
        return {
            payload: { groupId, meetingId },
            type: GroupActionTypes.FETCH_GROUP_MEETING_STARTED
        };
    },
    fetchGroupMeetingCompleted(groupId: string, meetingId: string, meeting: IMeeting): IFetchGroupMeetingCompleted {
        return {
            payload: {
                groupId,
                meeting,
                meetingId
            },
            type: GroupActionTypes.FETCH_GROUP_MEETING_COMPLETED
        };
    },
    fetchGroupMeetingFailed(groupId: string, meetingId: string, error: any): IFetchGroupMeetingFailed {
        return {
            payload: { groupId, meetingId },
            type: GroupActionTypes.FETCH_GROUP_MEETING_FAILED
        };
    },
    fetchGroupMeetingsStarted(groupId: string): IFetchGroupMeetingsStarted {
        return {
            payload: groupId,
            type: GroupActionTypes.FETCH_GROUP_MEETINGS_STARTED
        };
    },
    fetchGroupMeetingsCompleted(groupId: string, meetings: IMeeting[]): IFetchGroupMeetingsCompleted {
        return {
            payload: {
                id: groupId,
                meetings
            },
            type: GroupActionTypes.FETCH_GROUP_MEETINGS_COMPLETED
        };
    },
    fetchGroupMeetingsFailed(groupId: string, error: any): IFetchGroupMeetingsFailed {
        return {
            payload: groupId,
            type: GroupActionTypes.FETCH_GROUP_MEETINGS_FAILED
        };
    },
    addFavoriteDocumentStarted(groupId: string, meetingId: string, documentId: string): IAddFavoriteDocumentStarted {
        return {
            payload: { groupId, meetingId, documentId },
            type: GroupActionTypes.ADD_FAVORITE_DOCUMENT_STARTED
        };
    },
    addFavoriteDocumentCompleted(groupId: string, meetingId: string, documentId: string): IAddFavoriteDocumentCompleted {
        return {
            payload: { groupId, meetingId, documentId },
            type: GroupActionTypes.ADD_FAVORITE_DOCUMENT_COMPLETED
        };
    },
    addFavoriteDocumentFailed(groupId: string, meetingId: string, documentId: string): IAddFavoriteDocumentFailed {
        return {
            payload: { groupId, meetingId, documentId },
            type: GroupActionTypes.ADD_FAVORITE_DOCUMENT_FAILED
        };
    },
    removeFavoriteDocumentStarted(groupId: string, meetingId: string, documentId: string): IRemoveFavoriteDocumentStarted {
        return {
            payload: { groupId, meetingId, documentId },
            type: GroupActionTypes.REMOVE_FAVORITE_DOCUMENT_STARTED
        };
    },
    removeFavoriteDocumentCompleted(groupId: string, meetingId: string, documentId: string): IRemoveFavoriteDocumentCompleted {
        return {
            payload: { groupId, meetingId, documentId },
            type: GroupActionTypes.REMOVE_FAVORITE_DOCUMENT_COMPLETED
        };
    },
    removeFavoriteDocumentFailed(groupId: string, meetingId: string, documentId: string): IRemoveFavoriteDocumentFailed {
        return {
            payload: { groupId, meetingId, documentId },
            type: GroupActionTypes.REMOVE_FAVORITE_DOCUMENT_FAILED
        };
    },
    fetchGroupsStarted(): IFetchGroupsStarted {
        return {
            type: GroupActionTypes.FETCH_GROUPS_STARTED
        };
    },
    fetchGroupsCompleted(groups: IGroup[], isDi: boolean): IFetchGroupsCompleted {
        return {
            payload: { groups, isDi },
            type: GroupActionTypes.FETCH_GROUPS_COMPLETED
        };
    },
    fetchGroupsFailed(error: any): IFetchGroupsFailed {
        return {
            type: GroupActionTypes.FETCH_GROUPS_FAILED
        };
    },
    getGroupStarted(groupId: string): IGetGroupStarted {
        return {
            payload: groupId,
            type: GroupActionTypes.GET_GROUP_STARTED
        };
    },
    getGroupCompleted(groupId: string, group: IGroup): IGetGroupCompleted {
        return {
            payload: { groupId, group },
            type: GroupActionTypes.GET_GROUP_COMPLETED
        };
    },
    getGroupFailed(groupId: string, error: any): IGetGroupFailed {
        return {
            payload: {
                groupId,
                error
            },
            type: GroupActionTypes.GET_GROUP_FAILED
        };
    },
    setCurrentGroup(groupId: string): ISetCurrentGroup {
        return {
            payload: groupId,
            type: GroupActionTypes.SET_CURRENT_GROUP
        };
    }
};

export const fetchGroupMembers = (groupId: string, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.setCurrentGroup(groupId));
    dispatch(actionCreators.fetchGroupMembersStarted(groupId));

    return groupService.getGroupMembers(groupId, cancelTokenSource)
        .then(response => dispatch(actionCreators.fetchGroupMembersCompleted(groupId, response)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.fetchGroupMembersFailed(groupId, error)));
};

export const fetchGroupActivityStats = (groupId: string, from: Date, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.setCurrentGroup(groupId));

    return groupService.getGroupActivityStats(groupId, from, cancelTokenSource)
        .then(response => dispatch(actionCreators.fetchGroupActivityStatsCompleted(groupId, from, response)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.fetchGroupActivityStatsFailed(groupId, from, error)));
};

export const fetchGroupMeeting = (groupId: string, meetingId: string, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.setCurrentGroup(groupId));
    dispatch(actionCreators.fetchGroupMeetingStarted(groupId, meetingId));

    return meetingService.getGroupMeeting(groupId, meetingId, cancelTokenSource)
        .then(response => dispatch(actionCreators.fetchGroupMeetingCompleted(groupId, meetingId, response)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.fetchGroupMeetingFailed(groupId, meetingId, error)));
};

export const fetchGroupMeetings = (groupId: string, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.fetchGroupMeetingsStarted(groupId));

    return groupService.getGroupMeetings(groupId, cancelTokenSource)
        .then(response => dispatch(actionCreators.fetchGroupMeetingsCompleted(groupId, response)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.fetchGroupMeetingsFailed(groupId, error)));
};

export const addFavoriteDocument = (groupId: string, meetingId: string, documentId: string, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.addFavoriteDocumentStarted(groupId, meetingId, documentId));

    return meetingService.addFavoriteDocument(groupId, documentId, cancelTokenSource)
        .then(() => dispatch(actionCreators.addFavoriteDocumentCompleted(groupId, meetingId, documentId)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.addFavoriteDocumentFailed(groupId, meetingId, documentId)));
};

export const removeFavoriteDocument = (groupId: string, meetingId: string, documentId: string, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.removeFavoriteDocumentStarted(groupId, meetingId, documentId));

    return meetingService.removeFavoriteDocument(groupId, documentId, cancelTokenSource)
        .then(() => dispatch(actionCreators.removeFavoriteDocumentCompleted(groupId, meetingId, documentId)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.removeFavoriteDocumentFailed(groupId, meetingId, documentId)));
};

export const fetchGroupMessages = (groupId: string, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.fetchGroupMessagesStarted(groupId));

    return messagesService.getGroupMessages(groupId, cancelTokenSource)
        .then(response => dispatch(actionCreators.fetchGroupMessagesCompleted(groupId, response.hasResults ? response.results : [])))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.fetchGroupMessagesFailed(groupId, error)));
};

export const fetchGroups = (isDi: boolean, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.fetchGroupsStarted());

    return personService.getGroups(cancelTokenSource)
        .then(groups => dispatch(actionCreators.fetchGroupsCompleted(groups, isDi)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.fetchGroupsFailed(error)));
};

export const fetchGroup = (groupId: string, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.getGroupStarted(groupId));

    return groupService.getGroup(groupId, cancelTokenSource)
        .then(response => dispatch(actionCreators.getGroupCompleted(groupId, response)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.getGroupFailed(groupId, error)));
};

export const removeMeetingDocument = (groupId: string, meetingId: string, documentId: string, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.removeMeetingDocumentStarted(groupId, meetingId, documentId));

    return meetingService.removeMeetingDocument(groupId, meetingId, documentId, cancelTokenSource)
        .then(() => dispatch(actionCreators.removeMeetingDocumentCompleted(groupId, meetingId, documentId)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.removeMeetingDocumentFailed(groupId, meetingId, documentId, error)));
};

export const addFavoriteAttachment = (groupId: string, messageId: string, attachmentId: string, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.addFavoriteAttachmentStarted(groupId, messageId, attachmentId));

    return messagesService.addFavoriteAttachment(attachmentId, cancelTokenSource)
        .then(() => dispatch(actionCreators.addFavoriteAttachmentCompleted(groupId, messageId, attachmentId)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.addFavoriteAttachmentFailed(groupId, messageId, attachmentId)));
};

export const removeFavoriteAttachment = (groupId: string, messageId: string, attachmentId: string, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.removeFavoriteAttachmentStarted(groupId, messageId, attachmentId));

    return messagesService.removeFavoriteAttachment(attachmentId, cancelTokenSource)
        .then(() => dispatch(actionCreators.removeFavoriteAttachmentCompleted(groupId, messageId, attachmentId)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.removeFavoriteAttachmentFailed(groupId, messageId, attachmentId)));
};

export const signupPerson = (groupId: string, meetingId: string, personId: number, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.signupPersonStarted(groupId, meetingId, personId));

    return meetingService.signupPerson(groupId, meetingId, personId, cancelTokenSource)
        .then(() => dispatch(actionCreators.signupPersonCompleted(groupId, meetingId, personId)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.signupPersonFailed(groupId, meetingId, personId, error)));
};

export const cancelPerson = (groupId: string, meetingId: string, personId: number, cancelTokenSource: CancelTokenSource = axios.CancelToken.source()) => (dispatch: Dispatch<GroupActions>) => {
    dispatch(actionCreators.cancelPersonStarted(groupId, meetingId, personId));

    return meetingService.cancelPerson(groupId, meetingId, personId, cancelTokenSource)
        .then(() => dispatch(actionCreators.cancelPersonCompleted(groupId, meetingId, personId)))
        .catch(error => !axios.isCancel(error) && dispatch(actionCreators.cancelPersonFailed(groupId, meetingId, personId, error)));
};